var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
import { jsx as _jsx } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import React, { useContext } from "react";
import { useLocation, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQuery } from "@apollo/client/react";
import { AudioInstructions } from "@sequentech/ui-essentials";
import { EAudioInstructionsPolicy, EAudioInstructionsScreen, findAudioInstructionsRecording, } from "@sequentech/ui-core";
import { GET_DOCUMENT } from "../../queries/GetDocument";
import { GET_SUPPORT_MATERIALS } from "../../queries/GetSupportMaterials";
import { useGetPublicDocumentUrl } from "../../hooks/public-document-url";
import { SettingsContext } from "../../providers/SettingsContextProvider";
import { useAppSelector } from "../../store/hooks";
import { selectFirstBallotStyle } from "../../store/ballotStyles/ballotStylesSlice";
import { selectElectionEventById } from "../../store/electionEvents/electionEventsSlice";
var SCREEN_PATHS = [
    [/\/election-chooser\/?$/, EAudioInstructionsScreen.ELECTION_CHOOSER],
    [/\/materials\/?$/, EAudioInstructionsScreen.SUPPORT_MATERIALS],
    [/\/election\/[^/]+\/start\/?$/, EAudioInstructionsScreen.START],
    [/\/election\/[^/]+\/vote\/?$/, EAudioInstructionsScreen.BALLOT],
    [/\/election\/[^/]+\/review\/?$/, EAudioInstructionsScreen.REVIEW],
    [/\/election\/[^/]+\/confirmation\/?$/, EAudioInstructionsScreen.CONFIRMATION],
    [/\/election\/[^/]+\/audit\/?$/, EAudioInstructionsScreen.AUDIT],
    [/\/election\/[^/]+\/ballot-locator(\/[^/]*)?\/?$/, EAudioInstructionsScreen.BALLOT_LOCATOR],
];
/** The voter screen a path shows, for the screens that have audio instructions. */
export var audioInstructionsScreenAt = function (pathname) { var _a; return (_a = SCREEN_PATHS.find(function (_a) {
    var pattern = _a[0];
    return pattern.test(pathname);
})) === null || _a === void 0 ? void 0 : _a[1]; };
var SpokenInstructions = function (_a) {
    var policy = _a.policy, screen = _a.screen, recordingUrl = _a.recordingUrl;
    var _b = useTranslation(), t = _b.t, i18n = _b.i18n;
    return (_jsx(AudioInstructions, { policy: policy, text: t("audioInstructions.screens.".concat(screen), { defaultValue: "" }), language: i18n.language, recordingUrl: recordingUrl }));
};
/** Looks for the event's recording for the screen among its support materials. */
var RecordedInstructions = function (props) {
    var _a, _b, _c;
    var screen = props.screen, defaultLanguage = props.defaultLanguage;
    var i18n = useTranslation().i18n;
    var _d = useParams(), tenantId = _d.tenantId, eventId = _d.eventId;
    var getDocumentUrl = useGetPublicDocumentUrl().getDocumentUrl;
    var materials = useQuery(GET_SUPPORT_MATERIALS, {
        variables: { electionEventId: eventId !== null && eventId !== void 0 ? eventId : "", tenantId: tenantId !== null && tenantId !== void 0 ? tenantId : "" },
        skip: !eventId || !tenantId,
    }).data;
    var recording = findAudioInstructionsRecording((_a = materials === null || materials === void 0 ? void 0 : materials.sequent_backend_support_material) !== null && _a !== void 0 ? _a : [], screen, i18n.language, defaultLanguage);
    var documentId = (_b = recording === null || recording === void 0 ? void 0 : recording.document_id) !== null && _b !== void 0 ? _b : undefined;
    var documents = useQuery(GET_DOCUMENT, {
        variables: { ids: [documentId !== null && documentId !== void 0 ? documentId : ""], electionEventId: eventId, tenantId: tenantId !== null && tenantId !== void 0 ? tenantId : "" },
        skip: !documentId,
    }).data;
    var documentName = (_c = documents === null || documents === void 0 ? void 0 : documents.sequent_backend_document.find(function (document) { return document.id === documentId; })) === null || _c === void 0 ? void 0 : _c.name;
    return (_jsx(SpokenInstructions, __assign({}, props, { recordingUrl: documentId && documentName ? getDocumentUrl(documentId, documentName) : undefined })));
};
/**
 * The audio instructions for the screen the voter is on, where the event enables them.
 *
 * Without a session (the demo and the embedded preview) there is no recording to fetch, so
 * the text is spoken by the browser.
 */
export var ScreenAudioInstructions = function () {
    var _a, _b, _c;
    var pathname = useLocation().pathname;
    var eventId = useParams().eventId;
    var globalSettings = useContext(SettingsContext).globalSettings;
    var ballotStyle = useAppSelector(selectFirstBallotStyle);
    var electionEvent = useAppSelector(selectElectionEventById(eventId));
    var presentation = (_b = (_a = ballotStyle === null || ballotStyle === void 0 ? void 0 : ballotStyle.ballot_eml.election_event_presentation) !== null && _a !== void 0 ? _a : electionEvent === null || electionEvent === void 0 ? void 0 : electionEvent.presentation) !== null && _b !== void 0 ? _b : undefined;
    var policy = presentation === null || presentation === void 0 ? void 0 : presentation.audio_instructions_policy;
    var screen = audioInstructionsScreenAt(pathname);
    if (!policy || policy === EAudioInstructionsPolicy.DISABLED || !screen) {
        return null;
    }
    var props = {
        policy: policy,
        screen: screen,
        defaultLanguage: (_c = presentation === null || presentation === void 0 ? void 0 : presentation.language_conf) === null || _c === void 0 ? void 0 : _c.default_language_code,
    };
    return globalSettings.DISABLE_AUTH ? (_jsx(SpokenInstructions, __assign({}, props))) : (_jsx(RecordedInstructions, __assign({}, props)));
};
